import React from "react";
import { useState } from "react";

import { useParams } from "react-router-dom";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import axios from "axios";
import { KeyboardEvent } from "react";

import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";

import Button from "@mui/material/Button";
import LoadingButton from "@mui/lab/LoadingButton";
import { Helmet } from "react-helmet";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import LockResetOutlinedIcon from "@mui/icons-material/LockResetOutlined";

import Page from "../PageComponents/Page";
import PagePaper from "../PageComponents/PagePaper";

import { useNavigate } from "react-router-dom";
import Link from "@mui/material/Link";

import GetCSRFToken from "./GetCSRFToken";

function ResetPasswordPage() {
  const { uidb64, token } = useParams<{ uidb64: string; token: string }>();

  const [newPassword1, setNewPassword1] = useState("");
  const [newPassword2, setNewPassword2] = useState("");
  // TODO: use params and handle this better....clean up the close/open stuff
  const [wrongPasswordCount, setWrongPasswordCount] = useState(0); // TODO: after 2 or 3 do something else?
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showNewError, setShowNewError] = useState<string | undefined>(undefined);
  const [showConfirmError, setShowConfirmError] = useState<string | undefined>(undefined);
  const [generalError, setGeneralError] = useState<string | undefined>(undefined);

  const navigate = useNavigate();
  const RedirectSecondsConst = 5; // TODO: make this a drop-in component
  const [redirectSeconds, setRedirectSeconds] = useState(RedirectSecondsConst);
  const [redirectTimeout, setRedirectTimeout] = useState<NodeJS.Timeout | undefined>(undefined);
  const [countDownInterval, setCountDownInterval] = useState<NodeJS.Timeout | undefined>(undefined);

  function clearFields() {
    setNewPassword1("");
    setNewPassword2("");
  }

  function clearErrors() {
    setShowNewError(undefined);
    setShowConfirmError(undefined);
  }

  function handleCancel() {
    if (!loading) {
      clearFields();
      clearErrors();
    }
  }

  function cleanUp() {
    if (countDownInterval) {
      clearInterval(countDownInterval);
    }
    setCountDownInterval(undefined);
    setRedirectSeconds(RedirectSecondsConst);
  }

  function handleSubmit() {
    setLoading(true);
    clearErrors();
    axios
      .post(
        "/api/user/reset-password/complete",
        {
          password1: newPassword1,
          password2: newPassword2,
          uidb64: uidb64,
          token: token,
        },
        {
          headers: { "X-CSRFToken": GetCSRFToken() },
        }
      )
      .then((res) => {
        setSuccess(true);
        setLoading(false);
        setCountDownInterval(
          setInterval(() => {
            setRedirectSeconds((s) => Math.max(0, s - 1));
          }, 1000)
        );
        setRedirectTimeout(
          setTimeout(() => {
            cleanUp();
            navigate("/");
          }, RedirectSecondsConst * 1000)
        );
      })
      .catch((err) => {
        if (process.env.NODE_ENV === "development") {
          console.log(err);
        }
        clearFields();
        [
          {
            key: "new",
            fn: setShowNewError,
          },
          {
            key: "confirm",
            fn: setShowConfirmError,
          },
          {
            key: "general",
            fn: setGeneralError,
          },
        ].map((error_details_handlers) => error_details_handlers.fn(err.response.data[error_details_handlers.key]));
        setLoading(false);
      });
  }

  function catchEnterKeyDownToSubmit(e: KeyboardEvent) {
    if (e.key === "Enter") {
      handleSubmit();
    }
  }

  return (
    <Page pageLoading={false}>
      <Helmet>
        <title>Salus | Reset Password</title>
      </Helmet>
      <PagePaper title="Reset Password">
        {generalError === undefined ? (
          success ? (
            <Typography color="main" variant="body1">
              Success! Redirecting in {redirectSeconds} second{redirectSeconds === 1 ? "" : "s"}
              ...or click{" "}
              <Link
                onClick={() => {
                  if (redirectTimeout) {
                    clearTimeout(redirectTimeout);
                  }
                  cleanUp();
                  navigate("/");
                }}
              >
                here
              </Link>{" "}
              to be redirected now.
            </Typography>
          ) : (
            <FormControl
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                gap: 2,
                paddingTop: 1,
              }}
            >
              <TextField
                variant="outlined"
                type="password"
                error={showNewError !== undefined}
                autoComplete="new-password"
                fullWidth
                value={newPassword1}
                onChange={(newValue) => {
                  setShowNewError(undefined);
                  setNewPassword1(newValue.target.value);
                }}
                disabled={loading}
                onKeyDown={catchEnterKeyDownToSubmit}
                label="New Password"
                helperText={showNewError}
              />
              <TextField
                variant="outlined"
                type="password"
                error={showConfirmError !== undefined}
                autoComplete="new-password"
                fullWidth
                value={newPassword2}
                onChange={(newValue) => {
                  setShowConfirmError(undefined);
                  setNewPassword2(newValue.target.value);
                }}
                disabled={loading}
                onKeyDown={catchEnterKeyDownToSubmit}
                label="Confirm New Password"
                helperText={showConfirmError}
              />
            </FormControl>
          )
        ) : (
          <Typography color="error" variant="body1">
            {generalError}
          </Typography>
        )}

        <LoadingButton
          variant="contained"
          size="large"
          color={success ? "success" : undefined}
          endIcon={success ? <CheckIcon /> : <LockResetOutlinedIcon />}
          loading={loading && !success}
          onClick={() => {
            if (!success) {
              handleSubmit();
            }
          }}
          disabled={generalError !== undefined}
          sx={{ whiteSpace: "nowrap" }}
        >
          {success ? "Complete!" : "Complete"}
        </LoadingButton>
      </PagePaper>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/")} disabled={loading || success}>
        Back to Salus
      </Button>
    </Page>
  );
}

export default ResetPasswordPage;
